import { useCallback, useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, isSessionError, formatRupiah } from "@/lib/api";
import { TrendChart, StatusDonut, Breakdown, COLORS, LABEL, pct } from "@/components/admin/AnalyticsCharts";

const ymd = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
const daysAgo = (n) => ymd(new Date(Date.now() - n * 86400000));
const PRESETS = { today: ["Hari ini", 0], d7: ["7 hari", 6], d30: ["30 hari", 29] };
const GAMES = { royal_dream: "Royal Dream", higgs_domino: "Higgs Domino" };

const Delta = ({ now, prev }) => {
  if (!prev) return <span className="text-[11px] text-zinc-600">vs periode lalu: —</span>;
  const d = Math.round(((now - prev) / prev) * 100);
  const up = d >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] ${up ? "text-emerald-400" : "text-rose-400"}`}>
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}{Math.abs(d)}% vs periode lalu
    </span>
  );
};

const Kpi = ({ label, value, sub, delta, tone = "text-white", testid }) => (
  <div className="rk-glass rounded-2xl p-5" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className={`mt-2 text-2xl font-extrabold ${tone}`}>{value}</div>
    <div className="mt-1 flex flex-wrap items-center justify-between gap-1 text-xs text-zinc-400">{sub}{delta}</div>
  </div>
);

const Kpis = ({ s, prev, topup, bongkar }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
    <Kpi label="Total Transaksi" value={s.count.toLocaleString("id-ID")} sub={`Top Up ${topup.count} · Bongkar ${bongkar.count}`} delta={<Delta now={s.count} prev={prev.all.count} />} testid="kpi-total" />
    <Kpi label="Top Up Masuk (Sukses)" value={formatRupiah(topup.rp_success)} tone="text-[#ffd700]"
      sub={`Bongkar dibayar ${formatRupiah(bongkar.rp_success)} · ${bongkar.chip_b_success.toLocaleString("id-ID", { maximumFractionDigits: 2 })} B`}
      delta={<Delta now={topup.rp_success} prev={prev.topup.rp_success} />} testid="kpi-rupiah" />
    {["success", "rejected", "pending"].map((k) => (
      <Kpi key={k} label={LABEL[k]} value={`${pct(s[k], s.count)}%`} tone={{ success: "text-emerald-400", rejected: "text-rose-400", pending: "text-amber-400" }[k]}
        sub={`${s[k]} transaksi`} testid={`kpi-${k}`} />
    ))}
  </div>
);

const Period = ({ preset, range, onPreset, onRange }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="period-picker">
    {Object.entries(PRESETS).map(([k, [l]]) => (
      <button key={k} onClick={() => onPreset(k)} data-testid={`period-${k}`}
        className={`rounded-full px-4 py-2 text-xs transition-colors ${preset === k ? "bg-[#e5b80b] font-semibold text-black" : "border border-white/10 text-zinc-400 hover:text-white"}`}>{l}</button>
    ))}
    <span className="flex items-center gap-2">
      <input type="date" value={range[0]} max={range[1]} onChange={(e) => e.target.value && onRange([e.target.value, range[1]])} className="rk-input !w-auto !py-1.5 text-xs [color-scheme:dark]" data-testid="period-from" />
      <span className="text-zinc-600">–</span>
      <input type="date" value={range[1]} min={range[0]} max={ymd(new Date())} onChange={(e) => e.target.value && onRange([range[0], e.target.value])} className="rk-input !w-auto !py-1.5 text-xs [color-scheme:dark]" data-testid="period-to" />
    </span>
  </div>
);

export const ReportsPanel = () => {
  const [preset, setPreset] = useState("today");
  const [range, setRange] = useState([daysAgo(0), daysAgo(0)]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setBusy(true);
    api.get("/admin/analytics", { params: { date_from: range[0], date_to: range[1] } }).then((r) => setData(r.data))
      .catch((e) => !isSessionError(e) && toast.error(errMsg(e))).finally(() => setBusy(false));
  }, [range]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("jp:tx-new", load);
    return () => {
      clearInterval(t);
      window.removeEventListener("jp:tx-new", load);
    };
  }, [load]);

  const pickPreset = (k) => {
    setPreset(k);
    setRange([daysAgo(PRESETS[k][1]), daysAgo(0)]);
  };
  const s = data?.summary.all;

  return (
    <section className="space-y-5" data-testid="reports-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Period preset={preset} range={range} onPreset={pickPreset} onRange={(r) => { setPreset(""); setRange(r); }} />
        <span className="flex items-center gap-2 text-xs text-zinc-500">{busy ? <Loader2 size={12} className="animate-spin" /> : <span className="h-2 w-2 animate-pulse rounded-full" style={{ background: COLORS.success }} />} Live · WIB</span>
      </div>
      {!data ? <p className="text-sm text-zinc-500">Memuat…</p> : (
        <>
          <Kpis s={s} prev={data.previous} topup={data.summary.topup} bongkar={data.summary.bongkar} />
          <div className="grid gap-4 lg:grid-cols-3">
            <TrendChart data={data.trend} hourly={data.granularity === "hour"} />
            <StatusDonut s={data.summary.all} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <Breakdown title="Per Jenis" testid="break-kind" items={[["topup", "Top Up", data.summary.topup], ["bongkar", "Bongkar", data.summary.bongkar]]} />
            <Breakdown title="Per Game" testid="break-game" items={Object.keys(GAMES).filter((k) => data.by_game[k]).map((k) => [k, GAMES[k], data.by_game[k]])} />
            <Breakdown title="Member vs Inject" testid="break-source" items={["member", "inject"].filter((k) => data.by_source[k]).map((k) => [k, k === "inject" ? "Inject Manual" : "Member", data.by_source[k]])} />
          </div>
        </>
      )}
    </section>
  );
};
